<template>
  <div class="tags-admin">
    <!-- Header -->
    <div class="tags-header">
      <div class="tags-header-left">
        <div class="tag-count">
          <span class="count-number">{{ allTags.length }}</span>
          <span class="count-label">{{ allTags.length === 1 ? $t('tag') : $t('tags') }}</span>
        </div>
      </div>
      <div class="tags-header-right">
        <button
          v-if="!hideTagHelp"
          @click="dismissTagHelp"
          class="help-badge"
          :title="$t('Tags are visible only to project admins.')"
          v-tippy="{ placement: 'top' }"
        >
          <svg class="help-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <span>{{ $t('Admin only') }}</span>
        </button>
        <button
          class="add-tag-btn"
          @click.prevent="toggleInput"
          :title="$t('Add tag')"
          v-tippy="{ placement: 'top' }"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Tag Input (inline, toggled) -->
    <transition name="slide-down">
      <div v-if="inputVisible" class="tag-input-section">
        <div class="tag-input-wrapper">
          <svg class="tag-input-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
          </svg>
          <input
            class="tag-input"
            v-model="inputValue"
            ref="tagInput"
            :placeholder="$t('Type to search or add new...')"
            @keyup.enter="handleInputConfirm()"
            @keyup.esc="handleInputCancel()"
            @blur="handleBlur"
          />
          <button
            v-if="inputValue"
            @click="inputValue = ''"
            class="clear-input"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div
          v-if="error"
          class="tag-error"
        >{{ error }}</div>
        <!-- Suggestions Dropdown -->
        <transition name="dropdown">
          <ul
            v-if="tagSuggestions.length > 0"
            class="tag-suggestions"
          >
            <li
              v-for="suggestionItem in tagSuggestions"
              :key="suggestionItem.id"
              class="suggestion-item"
            >
              <a
                href="#"
                class="suggestion-link"
                @mousedown.prevent="pickSuggestion(suggestionItem.id, suggestionItem.name)"
              >
                <svg class="suggestion-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
                <span>{{ suggestionItem.name }}</span>
              </a>
            </li>
            <li v-if="inputValue && !suggestionMatchesInput" class="suggestion-item suggestion-item--create">
              <a
                href="#"
                class="suggestion-link suggestion-link--create"
                @mousedown.prevent="handleInputConfirm()"
              >
                <svg class="suggestion-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                <span>{{ $t('Create') }} "<strong>{{ inputValue }}</strong>"</span>
              </a>
            </li>
          </ul>
        </transition>
      </div>
    </transition>

    <!-- Tags List -->
    <div class="tags-body">
      <TransitionGroup name="tag-list" tag="div" class="tags-wrap">
        <div
          v-for="tag in allTags"
          :key="tag.id"
          class="tag-pill"
        >
          <svg class="tag-pill-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
          </svg>
          <span class="tag-pill-text">{{ tag.name }}</span>
          <button
            class="tag-remove"
            @click.prevent="handleClose(tag)"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </TransitionGroup>

      <!-- Empty State -->
      <div v-if="allTags.length === 0 && !inputVisible" class="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" />
        </svg>
        <p>{{ $t('No tags yet') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import throttle from "lodash/throttle";

export default {
  props: ["tags", "tags-url"],
  data: function () {
    return {
      allTags: this.tags,
      inputVisible: false,
      inputValue: "",
      tagSuggestions: [],
      hideTagHelp: false,
      error: ""
    };
  },

  mounted: function () {
    if (window.localStorage.getItem("hideFeedBearTagHelp")) {
      this.hideTagHelp = true;
    }
  },

  computed: {
    suggestionMatchesInput() {
      if (!this.inputValue) return false;
      return this.tagSuggestions.some(
        s => s.name.toLowerCase() === this.inputValue.toLowerCase()
      );
    }
  },

  methods: {
    dismissTagHelp() {
      this.hideTagHelp = true;
      window.localStorage.setItem("hideFeedBearTagHelp", true);
    },

    handleClose(tag) {
      this.destroyTag(tag);
    },

    toggleInput() {
      if (this.inputVisible) {
        this.handleInputCancel();
      } else {
        this.showInput();
      }
    },

    showInput() {
      this.inputVisible = true;
      this.$nextTick(() => {
        this.$refs.tagInput.focus();
        this.getTagSuggestions();
      });
    },

    postTag: function (name) {
      var params = new FormData();
      params.append("tag_name", name);

      Rails.ajax({
        url: this.tagsUrl,
        type: "POST",
        data: params,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          if (data.name) {
            this.allTags.push(data);
            this.error = "";
          } else {
            this.error = data.error;
          }
        },
        error: (data, status) => {
          console.log(data);
        },
      });
    },

    destroyTag: function (tag) {
      Rails.ajax({
        url: this.tagsUrl + "/" + tag.id,
        type: "DELETE",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.allTags.splice(this.allTags.indexOf(tag), 1);
        },
        error: (data, status) => {
          console.log(data);
        },
      });
    },

    handleInputConfirm() {
      if (this.inputValue) {
        this.postTag(this.inputValue);
      }

      this.inputVisible = false;
      this.inputValue = "";
    },

    handleInputCancel() {
      this.inputVisible = false;
      this.inputValue = "";
    },

    handleBlur(event) {
      if (this.inputValue || event.relatedTarget) {
      } else {
        this.inputVisible = false;
      }
    },

    getTagSuggestions: throttle(function () {
      Rails.ajax({
        url: this.tagsUrl + "?search_term=" + encodeURIComponent(this.inputValue),
        type: "GET",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.tagSuggestions = data;
        },
        error: (data, status) => {
          console.log(data);
        },
      });
    }, 500),

    pickSuggestion: function (tag_id, tag_name) {
      this.postTag(tag_name);
      this.inputVisible = false;
      this.inputValue = "";
    },
  },

  watch: {
    inputValue: function () {
      this.getTagSuggestions();
    },
  },
};
</script>

<style scoped lang="scss">
.tags-admin {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

// Header
.tags-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
  border-radius: 12px 12px 0 0;
}

.tags-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tags-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tag-count {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  background: #f3f4f6;
  border-radius: 6px;
}

.count-number {
  font-size: 14px;
  font-weight: 600;
  color: #404040;
}

.count-label {
  font-size: 12px;
  color: #737373;
}

.help-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #fde68a;
  }
}

.help-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.add-tag-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #737373;
  transition: all 0.15s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    border-color: var(--accent-color, #009be5);
    color: var(--accent-color, #009be5);
    background: #f0f9ff;
  }
}

// Input Section
.tag-input-section {
  padding: 10px 14px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
  position: relative;
}

.tag-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.tag-input-icon {
  position: absolute;
  left: 10px;
  width: 15px;
  height: 15px;
  color: #a3a3a3;
  pointer-events: none;
}

.tag-input {
  width: 100%;
  padding: 8px 32px 8px 34px;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  transition: all 0.15s ease;
  outline: none;

  &::placeholder {
    color: #a3a3a3;
  }

  &:focus {
    border-color: var(--accent-color, #009be5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color, #009be5) 15%, transparent);
  }
}

.clear-input {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #a3a3a3;
  transition: color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: #737373;
  }
}

.tag-error {
  margin-top: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

// Suggestions
.tag-suggestions {
  position: absolute;
  left: 14px;
  right: 14px;
  top: 100%;
  z-index: 20;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  max-height: 180px;
  overflow-y: auto;
}

.suggestion-item {
  margin: 0;
}

.suggestion-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #404040;
  text-decoration: none;
  transition: background 0.1s ease;

  &:hover {
    background: #f3f4f6;
    text-decoration: none;
    color: #404040;
  }
}

.suggestion-link--create {
  color: var(--accent-color, #009be5);

  &:hover {
    background: #f0f9ff;
    color: var(--accent-color, #009be5);
  }
}

.suggestion-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  opacity: 0.5;
}

// Tags Body
.tags-body {
  padding: 10px 14px;
  min-height: 44px;
}

.tags-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px 5px 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #404040;
  transition: all 0.15s ease;

  &:hover {
    background: #e5e7eb;
  }
}

.tag-pill-icon {
  width: 12px;
  height: 12px;
  color: #a3a3a3;
  flex-shrink: 0;
}

.tag-pill-text {
  font-weight: 500;
  white-space: nowrap;
}

.tag-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #a3a3a3;
  transition: color 0.15s ease;
  flex-shrink: 0;
  margin-left: 2px;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: #ef4444;
  }
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  color: #a3a3a3;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 6px;
    opacity: 0.4;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

// Transitions
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.tag-list-enter-active,
.tag-list-leave-active {
  transition: all 0.2s ease;
}

.tag-list-enter {
  opacity: 0;
  transform: scale(0.8);
}

.tag-list-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.tag-list-move {
  transition: transform 0.2s ease;
}
</style>
